import { UniqueWidgetRefreshContext } from 'App';
import useDataRefresh from 'hooks/useDataRefresh';
import { useContext, useEffect, useRef } from 'react';

/**
 * Connects the dashboard's existing refresh signals to one active report query.
 * Query hooks handle the first request and location changes; this hook only requests later refreshes.
 */
const useSpecialReportWidgetRefresh = ({ route, location, widgetRefresh, refetch, skip }) => {
  const scheduledRefreshes = useContext(UniqueWidgetRefreshContext);
  const { widgetRefresh: globalRefresh } = useDataRefresh();
  const locationKey = `${location?.city}-${location?.state}`;
  const scheduledRefresh = scheduledRefreshes?.[`${locationKey}-${route}`];
  const previous = useRef(null);

  useEffect(() => {
    const current = { route, locationKey, scheduledRefresh, widgetRefresh, globalRefresh };
    const last = previous.current;
    previous.current = current;

    // Ignore mounting and query changes: RTK Query already fetches those automatically.
    if (skip || !last || last.route !== route || last.locationKey !== locationKey) return;

    // The timestamp first appears when the widget mounts its update timer after loading.
    // That first value describes the schedule; only later changes should request data again.
    const hasScheduledRefresh =
      last.scheduledRefresh && scheduledRefresh && scheduledRefresh !== last.scheduledRefresh;
    const hasManualRefresh = ['widgetRefresh', 'globalRefresh'].some(
      (key) => current[key] && current[key] !== last[key],
    );
    if (hasScheduledRefresh || hasManualRefresh) refetch();
  }, [route, locationKey, scheduledRefresh, widgetRefresh, globalRefresh, refetch, skip]);
};

export default useSpecialReportWidgetRefresh;
